import { Suspense, useMemo, lazy } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Scroll from '~/components/scroll/scroll.js';
import disableBodyScroll from '~/utils/disable-body-scroll.js';
import customDesktopScroll from '~/components/scroll/custom-desktop-scroll.js';
import UIContext from '~/ui/ui-context.ts';
import useUIContext, { UIContextConstants } from '~/ui/use-ui-context.ts';
import {
	selectIsDesktop,
	selectShowLocaleSelector,
	selectIsBotRequest,
	selectGlobalNavigationEnabled
} from '~/components/brand/ducks/selectors.js';
import { isSmallerSelector } from '~/components/layout/mediaquery.js';
import SearchContainer from '~/components/search/search-container.js';
import { BiggerGlobalStyle, GlobalStyle } from './wrapper/global-styles.js';
import { animationSettings, motionVariants } from './motion-contants.js';
import NullLoader from '../../null-loader.js';
import { isNativeRegHeaderEnabled } from '~/components/app-bridge/app-properties.js';
import useCasinoLocation from '~/hooks/use-casino-location.js';
import useSearchLocation from '~/hooks/use-search-location.js';
import LiveCasinoCarousel from '~/components/live-casino/carousel/live-casino-carousel-async.js';
import CasinoCarousel from '~/components/casino/carousel/casino-carousel.js';
import CasinoCarouselSkeleton from '~/components/casino-shared/styles/casino-carousel-skeleton.js';
import useBannerTermsConditions from '~/components/casino-shared/banner/use-banner-terms-conditions.js';

const NestedHubHeader = lazy(() =>
	import(/* webpackChunkName: "global-navigation" */ '~/components/global-navigation/nested-hub-header')
);

const CONTEXT_KEYS = [UIContextConstants.SIDEBAR, UIContextConstants.OVERLAY, UIContextConstants.MODAL];

const SEARCH_MODAL_CAMPAIGN_DETAIL = '?modal=campaign-detail';

const Layout = ({ routes, wrappers }) => {
	const location = useLocation();
	const uiContext = useUIContext();
	const {
		hasSubHeader,
		hasHeroHeader,
		hasHeader,
		hasSmallerBrandingHeader,
		hasProductNav,
		hasStaticHeader,
		hasSidebar,
		hasSearchBar,
		hasAppBanner,
		hasFooter,
		hasOverlay,
		hasModal = true,
		showSidebar,
		showOverlay,
		showModal,
		nativeComponentEnabled,
		sidebarAnimationEnabled,
		contextRefs,
		hasNativeHeader
	} = uiContext;

	const {
		RoutesMain,
		RoutesSubHeader,
		RoutesSideBar,
		RoutesHeader,
		RoutesFooter,
		RoutesNavbar,
		RoutesOverlay,
		RoutesRegulationsHeader,
		RoutesHero,
		RoutesAppBanner,
		RoutesModal
	} = routes;

	const {
		HeaderWrapper,
		SearchBarWrapper,
		AppBannerWrapper,
		HeroWrapper,
		MainWrapper,
		OverlayWrapper,
		SidebarWrapper,
		ModalWrapper,
		SubWrapper,
		FooterWrapper,
		Wrapper,
		AppWrapper,
		RegulationsHeaderWrapper
	} = wrappers;

	const SidebarWithDisabledBodyScroll = useMemo(() => disableBodyScroll(SidebarWrapper), [SidebarWrapper]);
	const OverlayWithDisabledBodyScroll = useMemo(() => disableBodyScroll(OverlayWrapper), [OverlayWrapper]);

	const [sidebarUIContext, overlayUIContext, modalUIContext] = useMemo(
		() =>
			CONTEXT_KEYS.map(key => ({
				...uiContext,
				withinSidebar: key === UIContextConstants.SIDEBAR,
				within: key,
				contextRef: contextRefs[key]
			})),
		[uiContext, contextRefs]
	);

	const smaller = useSelector(isSmallerSelector);

	const showLocaleSelector = useSelector(selectShowLocaleSelector);

	const overlayAnimation = !nativeComponentEnabled || showLocaleSelector ? 'enter' : 'exitOverlay';

	const sidebarAnimation = sidebarAnimationEnabled ? 'enter' : 'enterWithoutAnimation';
	const sidebarExitAnimation = sidebarAnimationEnabled ? 'exitSidebar' : 'exitSidebarWithoutAnimation';

	const isDesktop = useSelector(selectIsDesktop);

	const isBotRequest = useSelector(selectIsBotRequest);

	const { isCasinoExploreLocation, isLiveCasinoExploreLocation } = useCasinoLocation();
	const { isSearchLocation } = useSearchLocation();

	const { isTermsConditionsEnabled } = useBannerTermsConditions();

	const globalNavigationEnabled = useSelector(selectGlobalNavigationEnabled);

	const Carousel = useMemo(() => {
		if (isCasinoExploreLocation) {
			return <CasinoCarousel />;
		}
		if (isLiveCasinoExploreLocation) {
			return <LiveCasinoCarousel />;
		}
	}, [isCasinoExploreLocation, isLiveCasinoExploreLocation]);

	return (
		<AppWrapper>
			{smaller ? <GlobalStyle /> : <BiggerGlobalStyle />}
			<Scroll attachToWindow />
			<Wrapper isBot={isBotRequest}>
				{(hasStaticHeader || isNativeRegHeaderEnabled()) && (
					<RegulationsHeaderWrapper>
						<Suspense fallback={<NullLoader />}>
							<RoutesRegulationsHeader />
						</Suspense>
					</RegulationsHeaderWrapper>
				)}
				{hasAppBanner && (
					<AppBannerWrapper>
						<Suspense fallback={<NullLoader />}>
							<RoutesAppBanner />
						</Suspense>
					</AppBannerWrapper>
				)}
				{hasHeader && !hasNativeHeader && (
					<HeaderWrapper hasSmallerBrandingHeader={hasSmallerBrandingHeader}>
						<Suspense fallback={<NullLoader />}>
							<RoutesHeader />
						</Suspense>
					</HeaderWrapper>
				)}
				{hasSearchBar && (
					<SearchBarWrapper>
						<Suspense fallback={<NullLoader />}>
							<SearchContainer />
							{hasProductNav && !isSearchLocation && <RoutesNavbar />}
						</Suspense>
					</SearchBarWrapper>
				)}
				{hasSubHeader && <RoutesSubHeader />}
				{hasHeroHeader && (
					<HeroWrapper className="hero" dataAt="hero-casino-wrapper">
						<Suspense
							fallback={
								<CasinoCarouselSkeleton
									isSmall={smaller}
									isTermsConditionsEnabled={isTermsConditionsEnabled}
								/>
							}
						>
							{Carousel}
						</Suspense>
						<Suspense fallback={<NullLoader />}>
							<RoutesHero />
						</Suspense>
					</HeroWrapper>
				)}
				<SubWrapper>
					<MainWrapper id="main-layout" data-at="main-layout">
						<Suspense fallback={<NullLoader />}>
							{hasProductNav && !smaller && <RoutesNavbar />}
							{globalNavigationEnabled && smaller && <NestedHubHeader />}
							<RoutesMain />
						</Suspense>
					</MainWrapper>
					{hasSidebar && (
						<UIContext.Provider value={sidebarUIContext}>
							<AnimatePresence mode="wait">
								{showSidebar && (
									<SidebarWithDisabledBodyScroll
										key="sidebar"
										id="sidebar"
										data-at="sidebar-layout"
										variants={motionVariants}
										initial="exitSidebar"
										animate={sidebarAnimation}
										exit={sidebarExitAnimation}
										transition={animationSettings}
										forceLock={showSidebar && smaller}
										lockOnHover={showSidebar && !smaller && isDesktop}
										ref={sidebarUIContext.contextRef}
										className="sidebar"
									>
										<Suspense fallback={<NullLoader />}>
											<RoutesSideBar />
										</Suspense>
									</SidebarWithDisabledBodyScroll>
								)}
							</AnimatePresence>
						</UIContext.Provider>
					)}
					{hasOverlay && (
						<UIContext.Provider value={overlayUIContext}>
							<AnimatePresence mode="wait">
								{(showOverlay || nativeComponentEnabled) && (
									<OverlayWithDisabledBodyScroll
										key="overlay"
										initial="exitOverlay"
										variants={motionVariants}
										animate={overlayAnimation}
										transition={animationSettings}
										forceLock={showOverlay && smaller}
										ref={overlayUIContext.contextRef}
										exit="exitOverlay"
									>
										<Suspense fallback={<NullLoader />}>
											<RoutesOverlay />
										</Suspense>
									</OverlayWithDisabledBodyScroll>
								)}
							</AnimatePresence>
						</UIContext.Provider>
					)}
					{hasModal && (
						<UIContext.Provider value={modalUIContext}>
							{showModal && (
								<ModalWrapper
									modalUIContext={modalUIContext}
									large={location?.search?.startsWith(SEARCH_MODAL_CAMPAIGN_DETAIL)}
									ref={modalUIContext.contextRef}
								>
									<Suspense fallback={<NullLoader />}>
										<RoutesModal />
									</Suspense>
								</ModalWrapper>
							)}
						</UIContext.Provider>
					)}
				</SubWrapper>
				{hasFooter && (
					<FooterWrapper>
						<Suspense fallback={<NullLoader />}>
							<RoutesFooter />
						</Suspense>
					</FooterWrapper>
				)}
			</Wrapper>
		</AppWrapper>
	);
};
export default customDesktopScroll(Layout);
